import {
  Button,
  Checkbox,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import FullscreenBackground from "@/features/Common/components/FullscreenBackground";
import {
  trackEvent,
  trackPageViewV2,
  trackUserInteraction,
} from "@/services/analytics-adapter";
import theme, { overrideDefaultLinkStyling } from "@/theme";
import StepsCounter from "../../../Common/components/StepsCounter";
import DefaultAccordion from "@/features/Common/components/DefaultAccordion";
import { getConfigurationVariable } from "@/services/configuration-adapter";
import { convertPxToRem } from "@/utils";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import { useEffect, useState } from "react";
import FullScreenIframeDialog from "@/features/Common/components/FullScreenIframeDialog";
import DefaultDialog from "@/features/Common/components/DefaultDialog";
import {
  generatePlatformTsAndCsLinkBasedOnUserSelectedLanguage,
  MembershipDependent,
  PathologyPanel,
  requiresPreparationInstructions,
} from "@/services/core-api-adapter";
import IconLoader from "@/features/Common/components/IconLoader";
import InfoContextPanel from "@/features/Common/components/InfoContextPanel";

interface PathologyPromptProps {
  panel: PathologyPanel;
  onBackButtonClick: () => void;
  onContinueButtonClick: () => void;
  selectedDependant?: MembershipDependent | null;
  isVoucherSelected?: boolean;
}

interface PathologyPromptDisclaimerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function PathologyPromptDisclaimerModal({
  isOpen,
  onClose,
}: PathologyPromptDisclaimerModalProps) {
  const { t } = useTranslation();

  const [isTermsAndConditionsDialogOpen, setIsTermsAndConditionsDialogOpen] =
    useState(false);

  function onTermsLinkClick(event: any) {
    event.preventDefault();
    setIsTermsAndConditionsDialogOpen(true);
    trackEvent({
      event: "action.termsAndConditionsLinkOpened",
      source: "Pathology prompt disclaimer modal",
    });
    trackUserInteraction({
      linkText: `Pathology prompt disclaimer | Terms and Conditions`,
      linkIntent: "navigational",
      linkScope: "button",
    });
  }

  function onTermsDialogClose() {
    setIsTermsAndConditionsDialogOpen(false);
  }

  return (
    <DefaultDialog
      label={t("PathologyPrompt.disclaimer.modal.title")}
      isOpen={isOpen}
      onClose={onClose}
      fullWidth={true}
    >
      <Stack textAlign="center" spacing={2}>
        <Typography variant="h3" fontWeight={600}>
          {t("PathologyPrompt.disclaimer.modal.title")}
        </Typography>

        <Typography variant="body2" color="inherit">
          <Trans i18nKey={"PathologyPrompt.disclaimer.modal.body"}></Trans>
        </Typography>

        <Typography variant="body2" color="inherit">
          <Trans i18nKey="PathologyPrompt.disclaimer.modal.footer">
            <Link
              sx={{ color: "primary", fontWeight: 900 }}
              href="/"
              onClick={onTermsLinkClick}
            ></Link>
          </Trans>
        </Typography>
      </Stack>

      <FullScreenIframeDialog
        title={t(
          "CollectTermsAcceptance.termsAndConditions.TERMS_AND_CONDITIONS_PLATFORM.documentTitle"
        )}
        isOpen={isTermsAndConditionsDialogOpen}
        onClose={onTermsDialogClose}
        url={generatePlatformTsAndCsLinkBasedOnUserSelectedLanguage()}
      />
    </DefaultDialog>
  );
}

export function PathologyPrompt({
  panel,
  onContinueButtonClick,
  onBackButtonClick,
  selectedDependant,
  isVoucherSelected = false,
}: PathologyPromptProps) {
  const { t } = useTranslation();

  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const [isConsentAccepted, setIsConsentAccepted] = useState(false);
  const [showHighlight, setShowHighlight] = useState(false);

  const [
    isDiscountTermsAndConditionsDialogOpen,
    setIsDiscountTermsAndConditionsDialogOpen,
  ] = useState(false);

  const [
    isVoucherTermsAndConditionsDialogOpen,
    setIsVoucherTermsAndConditionsDialogOpen,
  ] = useState(false);

  const discountTermsAndConditionsDocumentTitle = "Discount T&Cs";
  const discountTermsAndConditionsDocumentUrl =
    "https://d12f0harl9p4iu.cloudfront.net/contracts/Discounts_T%26Cs.html";

  const voucherTermsAndConditionsDocumentTitle = "Voucher T&Cs";
  const voucherTermsAndConditionsDocumentUrl =
    "https://d1cqzwpyvsxlnx.cloudfront.net/contracts/vouchering-terms-and-conditions.html";

  function onDiscountTermsDialogClose() {
    setIsDiscountTermsAndConditionsDialogOpen(false);
  }

  function onVoucherTermsDialogClose() {
    setIsVoucherTermsAndConditionsDialogOpen(false);
  }

  function onDiscountTermsLinkClick(event: any) {
    event.preventDefault();
    setIsDiscountTermsAndConditionsDialogOpen(true);
    trackEvent({
      event: "action.discountTermsAndConditionsLinkOpened",
      source: "Pathology prompt",
    });
    trackUserInteraction({
      linkText: `Pathology prompt | Discount Terms and Conditions`,
      linkIntent: "navigational",
      linkScope: "button",
    });
  }

  function onVoucherTermsLinkClick(event: any) {
    event.preventDefault();
    setIsVoucherTermsAndConditionsDialogOpen(true);
    trackEvent({
      event: "action.voucherTermsAndConditionsLinkOpened",
      source: "Pathology prompt",
    });
    trackUserInteraction({
      linkText: `Pathology prompt | Voucher Terms and Conditions`,
      linkIntent: "navigational",
      linkScope: "button",
    });
  }

  function onConsentAcceptToggle(event: any) {
    event.preventDefault();
    setIsConsentAccepted(event.target.checked);

    if (showHighlight === true) {
      setShowHighlight(!event.target.checked);
    }

    trackEvent({
      event: "action.consentAccepted",
      source: "Pathology prompt",
    });
    trackUserInteraction({
      linkText: `Pathology prompt | Consent accepted`,
      linkIntent: "confirmational",
      linkScope: "button",
    });
  }

  const PATHOLOGY_FACILITY_LINK =
    getConfigurationVariable("VITE_APP_UNU_HEALTH_PATHOLOGY_FACILITY_LINK") ||
    "https://www.ampath.co.za/labs";

  function onFindAFacilityClick() {
    trackEvent({
      event: "action.pathologyFindAFacilityClicked",
      source: "Pathology next steps prompt",
    });
  }

  function onViewDisclaimerClick() {
    setIsDisclaimerModalOpen(true);
  }

  function onContinue() {
    trackEvent({
      event: "action.nextStepsContinue",
      source: "Pathology prompt",
    });

    trackUserInteraction({
      linkText: `Pathology prompt | Next steps continue`,
      linkIntent: "navigational",
      linkScope: "button",
    });

    if (isConsentAccepted === false) {
      setShowHighlight(true);
    }

    if (isConsentAccepted === true) {
      onContinueButtonClick();
    }
  }

  function onBack() {
    trackEvent({
      event: "action.nextStepsBack",
      source: "Pathology prompt",
    });
    onBackButtonClick();
  }

  useEffect(() => {
    trackPageViewV2({
      pageName: `Pathology prompt | Next steps`,
      pageSubSection1: "Pathology",
      pageSubSection2: "Pathology prompt",
      pageCategory: "Pathology",
    });
  }, []);

  return (
    <>
      <Stack p={2} justifyContent="space-between" flexGrow={1} minHeight="100%">
        <Stack
          color="neutral.50"
          spacing={4}
          sx={{
            height: "100%",
          }}
        >
          <Typography variant="h1" color="info.main" textAlign="center">
            {t("PathologyPrompt.title")}
          </Typography>
          {selectedDependant && (
            <InfoContextPanel
              title={t("SelectedDependantPanel.title")}
              text={
                selectedDependant.firstName + " " + selectedDependant.lastName
              }
            />
          )}
          <Stack spacing={4}>
            <Stack
              bgcolor="neutral.50"
              borderRadius={theme.shape.borderRadius}
              p={4}
              spacing={2}
            >
              <Typography variant="h2">
                {t("PathologyPrompt.steps.title")}
              </Typography>

              {isVoucherSelected ? (
                <Stack direction="row">
                  <StepsCounter stepCounter={1} />
                  <Stack>
                    <Typography ml={2} variant="body1">
                      <Trans
                        i18nKey={"PathologyPrompt.steps.1.voucher.title"}
                      />
                    </Typography>
                    <Typography ml={2} variant="body2">
                      <Trans
                        i18nKey={"PathologyPrompt.steps.1.voucher.subtitle"}
                      >
                        <Link
                          sx={{
                            color: "primary",
                            fontWeight: 900,
                            textDecoration: "none",
                          }}
                          href="/"
                          onClick={onVoucherTermsLinkClick}
                        ></Link>
                      </Trans>
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <Stack direction="row" alignItems="center">
                  <StepsCounter stepCounter={1} />
                  <Stack sx={{ width: "100%" }}>
                    <DefaultAccordion
                      title={t(`PathologyPrompt.steps.1.title`, {
                        amountTotalFormatted: panel.amountTotalFormatted,
                      })}
                      aria-label={t(`PathologyPrompt.amountBreakdown.label`)}
                      key={t("PathologyPrompt.amountBreakdown.label")}
                      accordionStyles={{
                        textTransform: "capitalize",
                        boxShadow: "none",
                        width: "100%",
                        padding: "0",
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography variant="h4">
                          {t("PathologyPrompt.amountBreakdown.accordion.title")}
                        </Typography>
                        {panel.panelTestsAmountFormatted && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body1" textTransform="none">
                              {t(
                                "PathologyPrompt.amountBreakdown.panel.fee.title"
                              )}
                            </Typography>
                            <Typography
                              variant="body1"
                              color="primary"
                              fontWeight="bold"
                            >
                              {panel.panelTestsAmountFormatted}
                            </Typography>
                          </Stack>
                        )}
                        {panel.unuHandlingFeeAmountFormatted && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body1" textTransform="none">
                              {t(
                                "PathologyPrompt.amountBreakdown.unuPlatform.fee.title"
                              )}
                            </Typography>
                            <Typography
                              variant="body1"
                              color="primary"
                              fontWeight="bold"
                            >
                              {panel.unuHandlingFeeAmountFormatted}
                            </Typography>
                          </Stack>
                        )}
                        {panel.unuTransactionalFeeAmountFormatted && (
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body1" textTransform="none">
                              {t(
                                "PathologyPrompt.amountBreakdown.unuTransactional.fee.title"
                              )}
                            </Typography>
                            <Typography
                              variant="body1"
                              color="primary"
                              fontWeight="bold"
                            >
                              {panel.unuTransactionalFeeAmountFormatted}
                            </Typography>
                          </Stack>
                        )}
                        {panel.isDiscountApplied &&
                          panel.discountAmountFormatted && (
                            <Stack
                              color="neutral.light"
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Typography
                                color="inherit"
                                variant="body1"
                                textTransform="none"
                              >
                                {t(
                                  "PathologyPrompt.amountBreakdown.discount.fee.title"
                                )}
                              </Typography>
                              <Typography
                                variant="body1"
                                color="inherit"
                                fontWeight="bold"
                              >
                                -{panel.discountAmountFormatted}
                              </Typography>
                            </Stack>
                          )}
                      </Stack>
                    </DefaultAccordion>
                    {panel.isDiscountApplied && (
                      <Button
                        variant="text"
                        color="primary"
                        sx={{
                          ...overrideDefaultLinkStyling,
                          fontSize: convertPxToRem(12),
                          color: "primary.main",
                          justifyContent: "start",
                          padding: 0,
                          marginLeft: convertPxToRem(14),
                        }}
                        size="small"
                        onClick={onDiscountTermsLinkClick}
                      >
                        {t("PathologyPrompt.termsAndConditions.title")}
                      </Button>
                    )}
                  </Stack>
                </Stack>
              )}

              <Stack direction="row">
                <StepsCounter stepCounter={2} />
                <Typography ml={2} variant="body1">
                  <Trans i18nKey={"PathologyPrompt.steps.2.title"} />
                </Typography>
              </Stack>
              <Stack direction="row">
                <StepsCounter stepCounter={3} />
                <Stack ml={2} spacing={2}>
                  <Typography variant="body1">
                    <Trans i18nKey={"PathologyPrompt.steps.3.title"} />
                  </Typography>
                  <ButtonWithAnalytics
                    page="PathologyNextStepsPrompt"
                    intent="navigational"
                    text={t("PathologyPrompt.findAFacility.title")}
                    component="a"
                    variant="text"
                    role="link"
                    target="_blank"
                    size="small"
                    onClick={onFindAFacilityClick}
                    href={PATHOLOGY_FACILITY_LINK}
                    sx={{
                      ...overrideDefaultLinkStyling,
                      fontSize: convertPxToRem(12),
                      color: "primary.main",
                      justifyContent: "start",
                      padding: 0,
                    }}
                  >
                    {t("PathologyPrompt.findAFacility.title")}
                  </ButtonWithAnalytics>
                </Stack>
              </Stack>
              <Stack direction="row">
                <StepsCounter stepCounter={4} />
                <Typography ml={2} variant="body1">
                  <Trans i18nKey={"PathologyPrompt.steps.4.title"} />
                </Typography>
              </Stack>
              <Stack direction="row">
                <StepsCounter stepCounter={5} />
                <Typography ml={2} variant="body1">
                  <Trans i18nKey={"PathologyPrompt.steps.5.title"} />
                </Typography>
              </Stack>
              <Stack ml={2} direction="row" alignItems="center" spacing={2}>
                <Checkbox
                  inputProps={{
                    "aria-label": t("PathologyPrompt.consent.checkbox.label"),
                  }}
                  sx={{ padding: 0 }}
                  value={isConsentAccepted}
                  onChange={onConsentAcceptToggle}
                />
                <Stack direction="row">
                  <Typography color={showHighlight ? "error" : "neutral"}>
                    <Trans i18nKey="PathologyPrompt.consent.checkbox.title">
                      <Link
                        sx={{
                          color: "primary",
                          fontWeight: 900,
                          cursor: "pointer",
                          textDecoration: "none",
                        }}
                        onClick={onViewDisclaimerClick}
                      ></Link>
                    </Trans>
                  </Typography>
                </Stack>
              </Stack>

              {requiresPreparationInstructions(panel.panelChargeCode) && (
                <>
                  <Divider />
                  <Stack direction="row">
                    <IconLoader
                      icon="InformationSymbolFilledCircleIcon"
                      sx={{
                        color: "primary.700",
                        fontSize: 30,
                      }}
                    />
                    <Typography ml={1} variant="body1">
                      <Trans
                        i18nKey={
                          "PathologyPrompt.preparation.requirements.title"
                        }
                      />
                    </Typography>
                  </Stack>
                </>
              )}
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                onClick={onBack}
                color="info"
                fullWidth
                variant="outlined"
              >
                {t("common.backButton")}
              </Button>
              <Button size="small" fullWidth color="info" onClick={onContinue}>
                {t("common.continueButton")}
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <FullscreenBackground color="primary.main" />
        <PathologyPromptDisclaimerModal
          isOpen={isDisclaimerModalOpen}
          onClose={() => setIsDisclaimerModalOpen(false)}
        />
      </Stack>
      <FullScreenIframeDialog
        title={discountTermsAndConditionsDocumentTitle}
        isOpen={isDiscountTermsAndConditionsDialogOpen}
        onClose={onDiscountTermsDialogClose}
        url={discountTermsAndConditionsDocumentUrl}
      />
      {isVoucherSelected && (
        <FullScreenIframeDialog
          title={voucherTermsAndConditionsDocumentTitle}
          isOpen={isVoucherTermsAndConditionsDialogOpen}
          onClose={onVoucherTermsDialogClose}
          url={voucherTermsAndConditionsDocumentUrl}
        />
      )}
    </>
  );
}
